import { OrderApiDto, ProductApiDto, SkuApiDto } from '@b2x/storefront-api-js-client/src';
import { OrderPopulate, SearchOrdersOptions } from '@b2x/storefront-api-js-client/src/orders';
import React from 'react';

import { analytics } from './analytics/analytics';
import { ApiRequestConfig } from './api/useApiRequest';
import { useCartApi } from './api/useCartApi';
import { useOrdersApi } from './api/useOrdersApi';
import { useAppContext } from './AppContext';
import { useStable } from './util';

export interface UseLastOrderOptions extends SearchOrdersOptions {}

export const useLastOrder = (options?: UseLastOrderOptions, config?: ApiRequestConfig) => {
  const [lastProductsOrdered, setLastProductsOrdered] = React.useState<Array<ProductApiDto>>([]);
  const [fetching, setFetching] = React.useState<boolean>(false);
  const [lastOrder, setLastOrder] = React.useState<OrderApiDto>();

  const { session } = useAppContext();
  const { addSkus } = useCartApi();

  const { searchOrders } = useOrdersApi();
  options = useStable(options);
  config = useStable(config);

  React.useEffect(() => {
    const populate: OrderPopulate = {
      warehouseOrders: {
        orderedSkus: {
          sku: {
            price: true,
            product: {
              attributes: true,
              skus: {
                price: true,
              },
            },
          },
        },
      },
    };
    const searchOrdersOptions: SearchOrdersOptions = {
      code: options?.code,
      orderBy: options?.orderBy,
      orderingType: options?.orderingType,
      pageNum: options?.pageNum ?? 1,
      pageSize: options?.pageSize ?? 1,
      pending: options?.pending ?? false,
      populate: populate,
      state: ['ARRIVED'],
      //aggiungere limit
    };

    if (session?.userLogged) {
      setFetching(true);
      searchOrders(searchOrdersOptions, config)
        .then((response) => {
          if (response.data.items && response.data.items.length > 0) {
            // prendo il primo ordine
            const _lastOrder = response.data.items[0];
            setLastOrder(_lastOrder);

            const seen = new Set<string>();
            const products = (
              _lastOrder.warehouseOrders?.flatMap((wo) => wo.orderedSkus?.map((os) => os.sku?.product) ?? []) ?? []
            ).filter((p): p is ProductApiDto => {
              if (!p || seen.has(p.id) || (p.assembled === true && p.kit === false)) return false;
              seen.add(p.id);
              return true;
            });
            setLastProductsOrdered(products);
          }
        })
        .finally(() => {
          setFetching(false);
        });
    }
  }, [
    config,
    options?.code,
    options?.orderBy,
    options?.orderingType,
    options?.pageNum,
    options?.pageSize,
    options?.pending,
    searchOrders,
    session?.userLogged,
  ]);

  const addAllProductsToCart = React.useCallback(() => {
    if (lastOrder) {
      const orderedSkus = (lastOrder.warehouseOrders ?? []).reduce(
        (result, wo) => {
          for (const os of wo.orderedSkus ?? []) {
            const sku = os.sku;
            const product = sku?.product;
            const price = sku?.price?.value;

            if (
              sku &&
              product &&
              price !== undefined &&
              price > 0.01 &&
              !(product.assembled === true && product.kit === false)
            ) {
              result.push({ product, quantity: os.quantity, sku });
            }
          }
          return result;
        },
        [] as Array<{ product: ProductApiDto; quantity: number; sku: SkuApiDto }>,
      );

      if (orderedSkus.length > 0)
        return addSkus(orderedSkus, config).then((response) => {
          analytics.events.addToCart(
            response.eventId,
            orderedSkus.map((item) => ({
              listing: undefined,
              partialSku: item.sku,
              quantity: item.quantity,
            })),
          );
        });
    }
  }, [addSkus, config, lastOrder]);

  return { addAllProductsToCart, fetching, lastOrder, lastProductsOrdered };
};
